import { Formik } from "formik";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import * as Yup from "yup";
import Breadcrumbs from "../../../components/Breadcrumbs";
import Button from "../../../components/button";
import CnicInput from "../../../components/CnicInput";
import CustomModal from "../../../components/CustomModal";
import FileUpload from "../../../components/FileUpload";
import Input from "../../../components/input";
import PhoneNumberInput from "../../../components/PhoneNumberInput";
import Select from "../../../components/Select";
import { apiService } from "../../../services/apiService";
import {
  fetchThreePlTransporterDropdown,
  fetchTransporterCitiesDropdown,
  fetchTransporterVehicleTypesDropdown,
} from "../../../store/Slices/dropdownSlice";
import "../../../styles/Access.css";
import "../../../styles/AssetsRegistration.css";
import "../../../styles/Issue.css";
import "../../../styles/Trip.css";
import { findOptionByValueOrLabel } from "../../../utils/helper";
import { buildCnicSchema } from "../../../utils/pakistanCnic";
import { provinceOptions } from "../../../utils/provinceOptions";
import { plusIcon } from "../../../utils/svgs";
import { errorNotify, successNotify } from "../../../utils/toast";
import { buildPhoneNumberSchema } from "../../../pages/ThreePL/shared/phoneValidation";
import { vehicleTypeOptions } from "./vehicleAssets.constants";

const normalizeAssetsRegistrationTab = (value, fallback = "vehicles") => {
  const normalized = String(value || "").trim().toLowerCase();
  return ["vehicles", "equipments", "tyres"].includes(normalized)
    ? normalized
    : fallback;
};

const getAssetsRegistrationPath = (tab = "vehicles") =>
  `/assets?tab=${normalizeAssetsRegistrationTab(tab)}`;

const getAssetsRegistrationReturnTab = (location, fallback = "vehicles") => {
  const params = new URLSearchParams(location?.search || "");
  return normalizeAssetsRegistrationTab(
    params.get("tab") || location?.state?.activeTab || location?.state?.returnTab,
    fallback
  );
};

const mapFiles = (files = []) =>
  (files || [])
    .map((file) => file?.fileName || file?.file_path || file?.path || file?.name || file)
    .filter(Boolean);

const toLabel = (item) =>
  String(
    item?.label ??
    item?.name ??
    item?.title ??
    item?.company_name ??
    item?.transporter_name ??
    item?.value ??
    item ??
    ""
  ).trim();

const toPositiveId = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const mapDropdownOptions = (source = [], fallback = []) => {
  const rows = Array.isArray(source) && source.length ? source : fallback;

  return rows
    .map((item) => {
      const label = toLabel(item);
      if (!label) return null;

      const rawValue = item?.value ?? item?.id ?? item?.vehicle_type_id ?? label;
      const recordId = toPositiveId(rawValue);

      return {
        label,
        value: recordId || rawValue,
        recordId,
      };
    })
    .filter(Boolean);
};

const transporterStatusOptions = [
  { label: "Active", value: "active" },
  { label: "Inactive", value: "inactive" },
];

const fallbackCityOptions = [
  { label: "Islamabad", value: "islamabad" },
  { label: "Rawalpindi", value: "rawalpindi" },
  { label: "Lahore", value: "lahore" },
  { label: "Karachi", value: "karachi" },
  { label: "Faisalabad", value: "faisalabad" },
  { label: "Multan", value: "multan" },
  { label: "Peshawar", value: "peshawar" },
  { label: "Sialkot", value: "sialkot" },
];

const getInitialValues = () => ({
  transporter: "",
  registrationNumber: "",
  vehicleType: "",
  driverName: "",
  mobileNumber: "",
  cnic: "",
  driverDocument: [],
});

const create3PLVehicleSchema = Yup.object().shape({
  transporter: Yup.string().required("Transporter / Vendor is required"),
  registrationNumber: Yup.string().trim().required("Registration No. is required"),
  vehicleType: Yup.string().required("Vehicle Type is required"),
  driverName: Yup.string().trim().required("Driver Name is required"),
  mobileNumber: buildPhoneNumberSchema("Mobile No. is required"),
  cnic: buildCnicSchema("CNIC is required"),
  driverDocument: Yup.array()
    .test(
      "has-documents",
      "Driver CNIC / License is required",
      (value) => Array.isArray(value) && value.length > 0
    )
    .required("Driver CNIC / License is required"),
});

const transporterInitialValues = {
  transporterName: "",
  ntnTaxNo: "",
  status: "active",
  contactPerson: "",
  mobileNumber: "",
  email: "",
  address: "",
  city: "",
  province: "",
  postalCode: "",
};

const transporterSchema = Yup.object().shape({
  transporterName: Yup.string().trim().required("Transporter Name is required"),
  ntnTaxNo: Yup.string().trim().nullable(),
  status: Yup.string().required("Status is required"),
  contactPerson: Yup.string().trim().required("Contact Person is required"),
  mobileNumber: buildPhoneNumberSchema("Mobile No. is required"),
  email: Yup.string()
    .trim()
    .nullable()
    .transform((value, originalValue) =>
      String(originalValue || "").trim() ? value : null
    )
    .email("Invalid email address"),
  address: Yup.string().trim().required("Address is required"),
  city: Yup.string().nullable(),
  province: Yup.string().nullable(),
  postalCode: Yup.string().trim().nullable(),
});

const Create3PLVehicle = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const returnPath = getAssetsRegistrationPath(
    getAssetsRegistrationReturnTab(location, "vehicles")
  );
  const [showTransporterModal, setShowTransporterModal] = useState(false);

  const {
    threePlTransporterDropdownData = [],
    transporterCitiesDropdownData = [],
    transporterVehicleTypesDropdownData = [],
    loading: dropdownLoading,
  } = useSelector((state) => state.dropdowns);

  useEffect(() => {
    dispatch(fetchThreePlTransporterDropdown());
    dispatch(fetchTransporterCitiesDropdown());
    dispatch(fetchTransporterVehicleTypesDropdown());
  }, [dispatch]);

  const transporterOptions = useMemo(
    () => mapDropdownOptions(threePlTransporterDropdownData),
    [threePlTransporterDropdownData]
  );

  const vehicleTypeDropdownOptions = useMemo(
    () => mapDropdownOptions(transporterVehicleTypesDropdownData, vehicleTypeOptions),
    [transporterVehicleTypesDropdownData]
  );

  const cityDropdownOptions = useMemo(
    () => mapDropdownOptions(transporterCitiesDropdownData, fallbackCityOptions),
    [transporterCitiesDropdownData]
  );

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      const payload = {
        transporter_id: Number(values.transporter),
        registration_number: String(values.registrationNumber || "").trim(),
        vehicle_type_id: Number(values.vehicleType),
        driver_name: String(values.driverName || "").trim(),
        mobile_no: String(values.mobileNumber || "").trim(),
        cnic: String(values.cnic || "").trim(),
        files: mapFiles(values.driverDocument),
      };

      await apiService.addThreePlVehicle(payload);
      successNotify("3PL Vehicle created successfully");
      navigate(returnPath);
    } catch (error) {
      errorNotify(error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      initialValues={getInitialValues()}
      validationSchema={create3PLVehicleSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        setFieldTouched,
        isSubmitting,
      }) => {
        const updateDriverDocument = (updater) => {
          const nextFiles =
            typeof updater === "function"
              ? updater(values.driverDocument || [])
              : updater;
          setFieldValue("driverDocument", nextFiles);
          setFieldTouched("driverDocument", true, true);
        };

        return (
          <div className="create_vehicle_page create_threepl_vehicle_page">
            <Breadcrumbs values={["Assets", "Vehicles", "Create 3PL Vehicle"]} />

            <div className="page_header access_view">
              <h2>Create 3PL Vehicle</h2>

              <div className="d-flex align-items-center gap-2">
                <Button
                  iconButton
                  icon={plusIcon}
                  iconBtnText="Add Transporter"
                  onClick={() => setShowTransporterModal(true)}
                  type="button"
                  shouldRow={false}
                />
                <Button
                  cancelBtn={() => navigate(returnPath)}
                  cancelBtnText="Cancel"
                  submitBtn={handleSubmit}
                  submitText="Save"
                  type="button"
                  shouldRow={false}
                  loading={isSubmitting}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="page_container">
              <Row>
                <Col md={12}>
                  <div className="trip_information_main_div create_threepl_vehicle_shell">
                    <h5>3PL Vehicle Information</h5>

                    <div className="w-100 mb-3">
                      <h6 className="create_threepl_section_heading mt-3">
                        Vehicle Information
                      </h6>
                      <Row>
                        <Col md={6}>
                          <Select
                            label="Transporter / Vendor"
                            placeholder="Select Transporter"
                            options={transporterOptions}
                            loading={dropdownLoading}
                            value={findOptionByValueOrLabel(
                              transporterOptions,
                              values.transporter
                            )}
                            name="transporter"
                            onChange={(option) =>
                              setFieldValue("transporter", option ? option.value : "")
                            }
                            onBlur={() => setFieldTouched("transporter", true)}
                            error={errors.transporter}
                            touched={touched.transporter}
                            isSearch={false}
                          />
                        </Col>
                        <Col md={6}>
                          <Input
                            label="Registration No."
                            placeholder="e.g. TKA-123"
                            name="registrationNumber"
                            value={values.registrationNumber}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.registrationNumber}
                            touched={touched.registrationNumber}
                          />
                        </Col>
                        <Col md={6}>
                          <Select
                            label="Vehicle Type"
                            placeholder="Select Type"
                            options={vehicleTypeDropdownOptions}
                            loading={dropdownLoading}
                            value={findOptionByValueOrLabel(
                              vehicleTypeDropdownOptions,
                              values.vehicleType
                            )}
                            name="vehicleType"
                            onChange={(option) =>
                              setFieldValue("vehicleType", option ? option.value : "")
                            }
                            onBlur={() => setFieldTouched("vehicleType", true)}
                            error={errors.vehicleType}
                            touched={touched.vehicleType}
                            isSearch={false}
                          />
                        </Col>
                      </Row>

                      <div className="create_vehicle_divider" />
                      <h6 className="create_threepl_section_heading">
                        Driver Information
                      </h6>
                      <Row>
                        <Col md={4}>
                          <Input
                            label="Driver Name"
                            placeholder="e.g. Muhammad Ali"
                            name="driverName"
                            value={values.driverName}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            error={errors.driverName}
                            touched={touched.driverName}
                          />
                        </Col>
                        <Col md={4}>
                          <PhoneNumberInput
                            label="Mobile No."
                            placeholder="923XXXXXXXXX"
                            name="mobileNumber"
                            value={values.mobileNumber}
                            onBlur={handleBlur}
                            error={errors.mobileNumber}
                            touched={touched.mobileNumber}
                            setFieldValue={setFieldValue}
                          />
                        </Col>
                        <Col md={4}>
                          <CnicInput
                            label="CNIC"
                            name="cnic"
                            value={values.cnic}
                            onBlur={handleBlur}
                            error={errors.cnic}
                            touched={touched.cnic}
                            setFieldValue={setFieldValue}
                          />
                        </Col>
                      </Row>

                      <div className="create_vehicle_divider" />
                      <h6 className="create_threepl_section_heading">
                        Documents
                      </h6>
                      <Row>
                        <Col md={12}>
                          <div className="issue_file_upload_wrapper">
                            <label>
                              Driver CNIC / License
                              <span className="required_star">*</span>
                            </label>
                            <FileUpload
                              destination="3pl-vehicles"
                              files={values.driverDocument}
                              setFiles={updateDriverDocument}
                              maxFileSize={5}
                              maxFiles={5}
                              accept="image/*,.pdf"
                              className="issue_file_upload create_vehicle_upload"
                            />
                            {touched.driverDocument && errors.driverDocument && (
                              <div className="threepl_vehicle_error">
                                {errors.driverDocument}
                              </div>
                            )}
                          </div>
                        </Col>
                      </Row>

                      <div className="d-flex align-items-center gap-2 mt-4">
                        <Button
                          cancelBtn={() => navigate(returnPath)}
                          cancelBtnText="Cancel"
                          submitText="Save"
                          type="button"
                          shouldRow={false}
                          submitBtn={handleSubmit}
                          loading={isSubmitting}
                          disabled={isSubmitting}
                        />
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>

            <CustomModal
              show={showTransporterModal}
              handleClose={() => setShowTransporterModal(false)}
              heading="Add Transporter"
              size="lg"
              dialogClassName="threepl_transporter_modal"
            >
              <Formik
                initialValues={transporterInitialValues}
                validationSchema={transporterSchema}
                onSubmit={async (transporterValues, { setSubmitting, resetForm }) => {
                  try {
                    const addressParts = [
                      String(transporterValues.address || "").trim(),
                      String(transporterValues.city || "").trim(),
                      String(transporterValues.province || "").trim(),
                    ].filter(Boolean);

                    const payload = {
                      name: String(transporterValues.transporterName || "").trim(),
                      ntn_number: String(transporterValues.ntnTaxNo || "").trim() || undefined,
                      status: transporterValues.status || "active",
                      contact_person: String(transporterValues.contactPerson || "").trim(),
                      contact: String(transporterValues.mobileNumber || "").trim(),
                      email: String(transporterValues.email || "").trim() || undefined,
                      address: addressParts.join(", ") || undefined,
                    };

                    const response = await apiService.addThreePlTransporter(payload);
                    successNotify("Transporter created successfully");

                    const refreshed = await dispatch(fetchThreePlTransporterDropdown()).unwrap();
                    const createdId =
                      response?.data?.id ||
                      response?.id ||
                      (Array.isArray(refreshed?.data) ? refreshed.data[0]?.id : null) ||
                      (Array.isArray(refreshed) ? refreshed[0]?.id : null);

                    if (createdId) {
                      setFieldValue("transporter", createdId);
                    }

                    resetForm();
                    setShowTransporterModal(false);
                  } catch (error) {
                    errorNotify(error);
                  } finally {
                    setSubmitting(false);
                  }
                }}
              >
                {({
                  values: transporterValues,
                  errors: transporterErrors,
                  touched: transporterTouched,
                  handleChange: handleTransporterChange,
                  handleBlur: handleTransporterBlur,
                  handleSubmit: handleTransporterSubmit,
                  setFieldValue: setTransporterFieldValue,
                  setFieldTouched: setTransporterFieldTouched,
                  isSubmitting: transporterSubmitting,
                }) => (
                  <div className="threepl_transporter_modal_body">
                    <h6 className="create_threepl_section_heading mt-3">
                      Transporter Information
                    </h6>
                    <Row>
                      <Col md={6}>
                        <Input
                          label="Transporter Name"
                          placeholder="Enter transporter name"
                          name="transporterName"
                          value={transporterValues.transporterName}
                          onChange={handleTransporterChange}
                          onBlur={handleTransporterBlur}
                          error={transporterErrors.transporterName}
                          touched={transporterTouched.transporterName}
                        />
                      </Col>
                      <Col md={6}>
                        <Input
                          label="NTN / Tax No."
                          placeholder="e.g. 1234567-8"
                          name="ntnTaxNo"
                          value={transporterValues.ntnTaxNo}
                          onChange={handleTransporterChange}
                          onBlur={handleTransporterBlur}
                          error={transporterErrors.ntnTaxNo}
                          touched={transporterTouched.ntnTaxNo}
                          required={false}
                        />
                      </Col>
                      <Col md={6}>
                        <Select
                          label="Status"
                          placeholder="Select Status"
                          options={transporterStatusOptions}
                          value={findOptionByValueOrLabel(
                            transporterStatusOptions,
                            transporterValues.status
                          )}
                          name="status"
                          onChange={(option) =>
                            setTransporterFieldValue("status", option ? option.value : "")
                          }
                          onBlur={() => setTransporterFieldTouched("status", true)}
                          error={transporterErrors.status}
                          touched={transporterTouched.status}
                          isSearch={false}
                        />
                      </Col>
                      <Col md={6}>
                        <Input
                          label="Contact Person"
                          placeholder="Enter contact person"
                          name="contactPerson"
                          value={transporterValues.contactPerson}
                          onChange={handleTransporterChange}
                          onBlur={handleTransporterBlur}
                          error={transporterErrors.contactPerson}
                          touched={transporterTouched.contactPerson}
                        />
                      </Col>
                      <Col md={6}>
                        <PhoneNumberInput
                          label="Mobile No."
                          placeholder="923XXXXXXXXX"
                          name="mobileNumber"
                          value={transporterValues.mobileNumber}
                          onBlur={handleTransporterBlur}
                          error={transporterErrors.mobileNumber}
                          touched={transporterTouched.mobileNumber}
                          setFieldValue={setTransporterFieldValue}
                        />
                      </Col>
                      <Col md={6}>
                        <Input
                          label="Email"
                          placeholder="name@email.com"
                          name="email"
                          value={transporterValues.email}
                          onChange={handleTransporterChange}
                          onBlur={handleTransporterBlur}
                          error={transporterErrors.email}
                          touched={transporterTouched.email}
                          required={false}
                        />
                      </Col>
                    </Row>

                    <div className="create_vehicle_divider" />
                    <h6 className="create_threepl_section_heading">Address</h6>
                    <Row>
                      <Col md={12}>
                        <Input
                          label="Address"
                          placeholder="Enter complete address"
                          name="address"
                          value={transporterValues.address}
                          onChange={handleTransporterChange}
                          onBlur={handleTransporterBlur}
                          error={transporterErrors.address}
                          touched={transporterTouched.address}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          label="City"
                          placeholder="Select city"
                          options={cityDropdownOptions}
                          loading={dropdownLoading}
                          value={findOptionByValueOrLabel(
                            cityDropdownOptions,
                            transporterValues.city
                          )}
                          name="city"
                          onChange={(option) =>
                            setTransporterFieldValue("city", option ? option.value : "")
                          }
                          onBlur={() => setTransporterFieldTouched("city", true)}
                          error={transporterErrors.city}
                          touched={transporterTouched.city}
                          isSearch={false}
                        />
                      </Col>
                      <Col md={4}>
                        <Select
                          label="Province"
                          placeholder="Select province"
                          options={provinceOptions}
                          value={findOptionByValueOrLabel(
                            provinceOptions,
                            transporterValues.province
                          )}
                          name="province"
                          onChange={(option) =>
                            setTransporterFieldValue("province", option ? option.value : "")
                          }
                          onBlur={() => setTransporterFieldTouched("province", true)}
                          error={transporterErrors.province}
                          touched={transporterTouched.province}
                          isSearch={false}
                        />
                      </Col>
                      <Col md={4}>
                        <Input
                          label="Postal Code"
                          placeholder="e.g. 74000"
                          name="postalCode"
                          value={transporterValues.postalCode}
                          onChange={handleTransporterChange}
                          onBlur={handleTransporterBlur}
                          error={transporterErrors.postalCode}
                          touched={transporterTouched.postalCode}
                          required={false}
                        />
                      </Col>
                    </Row>

                    <div className="threepl_transporter_modal_actions">
                      <Button
                        cancelBtn={() => setShowTransporterModal(false)}
                        cancelBtnText="Cancel"
                        submitText="Save Transporter"
                        submitBtn={handleTransporterSubmit}
                        type="button"
                        shouldRow={false}
                        loading={transporterSubmitting}
                        disabled={transporterSubmitting}
                      />
                    </div>
                  </div>
                )}
              </Formik>
            </CustomModal>
          </div>
        );
      }}
    </Formik>
  );
};

export default Create3PLVehicle;
